/**
 * Category Tree Select
 *
 * A cascading drill-down over the catalogue taxonomy: one <Select> per level,
 * each listing only the children of the level above it.
 *
 * Why cascading rather than one indented list. Plan 003 took the taxonomy from
 * 5 categories to 674 (40 L1, 470 L2, 164 L3). A single Select holding all of
 * them is unusable however it is labelled -- indenting 674 rows still means
 * scrolling 674 rows -- and there is no combobox or command palette in either
 * `components/ui` or `@finishd/shared-ui` to search them with, and adding a
 * dependency is out of scope. Cascading needs nothing new: it shows 40 rows,
 * then at most 19 (the widest L1 is Lighting), then a handful. The chosen
 * branch also stays visible above the current level, which an indented flat
 * list loses the moment it is scrolled.
 *
 * Why only leaves filter. Products are filed exclusively on leaf categories, so
 * an L1 like "Civil & Structural" has no products of its own. Selecting a
 * branch therefore drills in rather than filtering, and no leaf id is emitted
 * until a leaf is reached -- what happens to the caller's value in the meantime
 * depends on `mode`, see `CategoryTreeSelectMode`. The alternative -- filtering
 * by the branch's whole
 * subtree -- is closed off by the backend: `searchProductsQuerySchema` types
 * `categoryId` as a single uuid, so a subtree filter would need it widened to a
 * repeated param first. Silently filtering on a branch id instead would return
 * an empty grid, which reads as a bug rather than as "keep going".
 */

import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import type { CatalogCategoryTreeNode } from "../services/catalog-api.service";
import {
	type CategoryTreeIndex,
	getCategoryPath,
	indexCategoryTree,
	isLeafCategory,
} from "../utils/category-tree";

/**
 * Radix rejects an empty string as an item value, so the "nothing chosen" row
 * at each level carries a sentinel instead.
 */
const NONE_VALUE = "__none__";

/**
 * The taxonomy is capped at three levels (`MAX_CATEGORY_DEPTH` on the backend),
 * so these cover it; the fallback is only there so a deeper tree degrades to a
 * numbered label rather than to `undefined`.
 */
const LEVEL_LABELS = [
	{ key: "catalog.category", fallback: "Category" },
	{ key: "catalog.subcategory", fallback: "Subcategory" },
	{ key: "catalog.categoryType", fallback: "Type" },
] as const;

/**
 * What `value` means to the caller, and therefore what picking a branch does to
 * it. Drilling through a branch is a navigation step, not an edit, so the only
 * question is whether the caller can afford to have its value emptied while the
 * user is part-way down.
 *
 * - `"filter"` (browse): `value` is a filter nobody owns. A branch selection
 *   emits `""`, because "no category filter" is exactly what a half-drilled
 *   picker is applying, and the widened grid is visible feedback.
 * - `"assign"` (custom-product form): `value` is a saved field on a record.
 *   Emitting `""` there is destructive -- `onChange` is the record's setter, so
 *   opening the level-1 select to look around and then saving would blank a
 *   category the user never meant to touch. A branch selection therefore leaves
 *   the last committed leaf alone and is reported through
 *   `onPendingBranchChange` instead, so the form can refuse to save a
 *   half-drilled selection rather than silently save the stale one.
 *
 * The mode lives on the component rather than in each caller because the
 * drill-down state (`pathIds`) is the thing that has to diverge from `value`,
 * and it is private to the component; a caller holding its own draft would have
 * to duplicate the whole path-resolution to know when the draft became real.
 */
export type CategoryTreeSelectMode = "filter" | "assign";

export interface CategoryTreeSelectProps {
	/** Roots of the tree, as returned by `getCategoryTree`. */
	tree: CatalogCategoryTreeNode[];
	/** The chosen leaf category id, or "" for none. */
	value: string;
	/**
	 * Fires with a leaf id, and with "" when the user picks the outermost
	 * "nothing chosen" row. In `"filter"` mode it also fires with "" when the
	 * selection lands on a branch; in `"assign"` mode it does not fire at all
	 * for a branch -- see `CategoryTreeSelectMode`.
	 */
	onChange: (categoryId: string) => void;
	/** Label on the first level's "nothing chosen" row. */
	noneLabel?: string;
	/** How a branch selection affects `value`. Defaults to `"filter"`. */
	mode?: CategoryTreeSelectMode;
	/**
	 * Fires with the id of the branch the user has stopped on, and with "" once
	 * the selection is a leaf or empty. An `"assign"` form uses this to block
	 * save while the drill-down is half-finished: a product has to end up on a
	 * leaf, and the id that would be saved is not the one on screen.
	 *
	 * Must be referentially stable (a `useState` setter, or `useCallback`); it
	 * is an effect dependency.
	 */
	onPendingBranchChange?: (branchId: string) => void;
}

export function CategoryTreeSelect({
	tree,
	value,
	onChange,
	noneLabel,
	mode = "filter",
	onPendingBranchChange,
}: CategoryTreeSelectProps) {
	const { t } = useTranslation();
	const index = useMemo(() => indexCategoryTree(tree), [tree]);

	// The ids of the nodes chosen so far, outermost first. This is drill-down
	// state, not filter state: it holds a branch the user is part-way through
	// even though no filter is applied yet.
	const [pathIds, setPathIds] = useState<string[]>([]);

	// `value` wins whenever it is set, so a shared URL, the back button or a
	// record loaded from the API reopens the drill-down on the right branch --
	// including on the render where `tree` finally arrives and the id becomes
	// resolvable.
	//
	// The clearing arm is deliberately conditional. In `"filter"` mode, changing
	// the level-1 select to another branch sets `pathIds` and clears `value` in
	// the same render, so an unconditional reset would immediately undo the
	// branch the user just picked. A cleared `value` whose path still ends on a
	// leaf can only have come from outside the component -- "Clear filters" --
	// and that is the case worth reacting to.
	//
	// In `"assign"` mode a branch selection leaves `value` alone, so this effect
	// simply does not re-run and `pathIds` is free to sit on a branch while
	// `value` still holds the last committed leaf. That divergence is the point:
	// it is what "mid-drill" is.
	useEffect(() => {
		if (value) {
			setPathIds(getCategoryPath(index, value).map((node) => node.id));
			return;
		}
		setPathIds((prev) => {
			const tail = index.get(prev[prev.length - 1] ?? "");
			return tail && isLeafCategory(tail.node) ? [] : prev;
		});
	}, [value, index]);

	const path = useMemo(() => resolvePath(index, pathIds), [index, pathIds]);
	const selected = path[path.length - 1];

	// The drill-down has stopped on a branch: nothing is being filtered, and
	// nothing may be saved. Derived rather than stored so it cannot drift from
	// the selection it describes.
	const pendingBranchId =
		selected && !isLeafCategory(selected) ? selected.id : "";

	useEffect(() => {
		onPendingBranchChange?.(pendingBranchId);
	}, [pendingBranchId, onPendingBranchChange]);

	// One select for the roots, then one more for each chosen node that still
	// has children. A chosen leaf adds nothing, which is what ends the chain.
	const levels: CatalogCategoryTreeNode[][] = [tree];
	for (const node of path) {
		if (!isLeafCategory(node)) {
			levels.push(node.children);
		}
	}

	function handleLevelChange(level: number, nextValue: string): void {
		// Everything below the level being changed is no longer reachable.
		const prefix = pathIds.slice(0, level);
		const nextPathIds =
			nextValue === NONE_VALUE ? prefix : [...prefix, nextValue];
		setPathIds(nextPathIds);

		const tail = index.get(nextPathIds[nextPathIds.length - 1] ?? "")?.node;

		if (!tail) {
			// The "nothing chosen" row on the outermost level, which is the one
			// selection that means "no category" rather than "keep going". Both
			// modes emit it: in `"assign"` it is the only way to unset a category
			// on purpose, and it has to stay reachable.
			onChange("");
			return;
		}

		if (isLeafCategory(tail)) {
			onChange(tail.id);
			return;
		}

		// A branch, including the "All of X" row on an inner level. `"filter"`
		// drops the filter; `"assign"` holds on to the last committed leaf and
		// lets `pendingBranchId` tell the form the selection is not finished.
		if (mode === "filter") {
			onChange("");
		}
	}

	return (
		<div className="space-y-3">
			{levels.map((options, level) => {
				const parent = path[level - 1];
				const label = LEVEL_LABELS[level] ?? {
					key: "catalog.categoryLevel",
					fallback: `Level ${level + 1}`,
				};
				const levelNoneLabel = parent
					? `All of ${parent.name}`
					: (noneLabel ?? "All Categories");

				return (
					<div
						// Keyed on the parent rather than the level so that switching
						// branches remounts the child select instead of leaving the
						// previous branch's chosen row showing under a new parent.
						key={parent?.id ?? "__root__"}
						className="space-y-2"
					>
						<span
							style={{
								display: "block",
								fontSize: 13,
								fontWeight: 500,
								color: "var(--color-dashboard-muted-dark)",
							}}
						>
							{t(label.key, label.fallback)}
						</span>
						<Select
							value={pathIds[level] ?? NONE_VALUE}
							onValueChange={(next) => handleLevelChange(level, next)}
						>
							<SelectTrigger
								className="w-full"
								aria-label={t(label.key, label.fallback)}
								style={{
									borderRadius: 7,
									borderColor: "var(--color-border-default)",
									background: "var(--color-dashboard-paper)",
								}}
							>
								<SelectValue placeholder={levelNoneLabel} />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value={NONE_VALUE}>{levelNoneLabel}</SelectItem>
								{options.map((option) => (
									<SelectItem key={option.id} value={option.id}>
										{option.name}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>
				);
			})}

			{pendingBranchId && (
				<p
					style={{
						fontSize: 12,
						lineHeight: 1.4,
						// In "assign" this is a validation message, not a nudge: the form
						// will not save until it clears, so it carries the warmer colour.
						color:
							mode === "assign"
								? "var(--color-dashboard-accent-dark)"
								: "var(--color-dashboard-muted)",
					}}
				>
					{mode === "assign"
						? t(
								"catalog.pickLeafCategoryToSave",
								"Products are filed on the deepest level only. Narrow this down before saving.",
							)
						: t(
								"catalog.pickLeafCategory",
								"Products are filed on the deepest level only. Narrow this down to filter.",
							)}
				</p>
			)}
		</div>
	);
}

/**
 * Turn stored ids back into nodes, stopping at the first one the tree does not
 * know. A truncated path is better than a path with a hole in it: the levels
 * below a missing node describe a branch that no longer exists.
 */
function resolvePath(
	index: CategoryTreeIndex,
	pathIds: readonly string[],
): CatalogCategoryTreeNode[] {
	const nodes: CatalogCategoryTreeNode[] = [];
	for (const id of pathIds) {
		const entry = index.get(id);
		if (!entry) break;
		nodes.push(entry.node);
	}
	return nodes;
}
